import { useState, useEffect } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import { Button } from "../../../../components/ui/button";
import { Switch } from "../../../../components/ui/switch";
import {
  ChevronUp,
  ChevronDown,
  Trash2,
  Plus,
  GripVertical,
  X,
  Type,
  Calendar,
  User,
  List,
  Pencil,
  Settings,
  Layers,
} from "lucide-react";
import { FieldGroup } from "../../../../models/audit/library";
import {
  useUpdateFieldGroup,
  useCreateFieldDefinition,
  useUpdateFieldDefinition,
  useDeleteFieldDefinition,
} from "../../../../hooks/useLibraries";

interface LibrarySettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fieldGroup: FieldGroup;
  onSuccess: () => void;
}

type FieldTypeValue = "text" | "user" | "date";
type TabValue = "general" | "fields";

interface FieldConfig {
  max_length?: number;
  allow_multiple?: boolean;
  include_time?: boolean;
}

interface LocalFieldDefinition {
  id: string;
  name: string;
  fieldType: FieldTypeValue;
  fieldConfig: FieldConfig;
  isRequired: boolean;
  displayOrder: number;
  isNew?: boolean;
  isEditing?: boolean;
}

const FIELD_TYPE_CONFIG: Record<
  FieldTypeValue,
  { label: string; icon: React.ReactNode; description: string }
> = {
  text: {
    label: "Text",
    icon: <Type className="h-4 w-4" />,
    description: "Single or multi-line text",
  },
  user: {
    label: "User",
    icon: <User className="h-4 w-4" />,
    description: "User selector",
  },
  date: {
    label: "Date",
    icon: <Calendar className="h-4 w-4" />,
    description: "Date picker",
  },
};

export function LibrarySettingsModal({
  open,
  onOpenChange,
  fieldGroup,
  onSuccess,
}: LibrarySettingsModalProps) {
  const [activeTab, setActiveTab] = useState<TabValue>("general");

  // General settings state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [slugPrefix, setSlugPrefix] = useState("");

  // Fields state
  const [fields, setFields] = useState<LocalFieldDefinition[]>([]);
  const [editingField, setEditingField] = useState<LocalFieldDefinition | null>(
    null,
  );
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Hooks
  const { updateFieldGroup, loading: loadingUpdate } = useUpdateFieldGroup();
  const { createFieldDefinition, loading: loadingCreate } =
    useCreateFieldDefinition();
  const { updateFieldDefinition, loading: loadingFieldUpdate } =
    useUpdateFieldDefinition();
  const { deleteFieldDefinition, loading: loadingDelete } =
    useDeleteFieldDefinition();

  const loading =
    loadingUpdate || loadingCreate || loadingFieldUpdate || loadingDelete;

  // Initialize general settings
  useEffect(() => {
    if (open && fieldGroup) {
      setName(fieldGroup.name);
      setDescription(fieldGroup.description ?? "");
      setSlugPrefix(fieldGroup.slugPrefix);
    }
  }, [open, fieldGroup]);

  // Initialize fields
  useEffect(() => {
    if (open && fieldGroup.fieldDefinitions) {
      const sortedFields = [...fieldGroup.fieldDefinitions].sort(
        (a, b) => a.displayOrder - b.displayOrder,
      );
      setFields(
        sortedFields.map((f) => ({
          id: f.id,
          name: f.name,
          fieldType: f.fieldType as FieldTypeValue,
          fieldConfig: (f.fieldConfig ?? {}) as FieldConfig,
          isRequired: f.isRequired,
          displayOrder: f.displayOrder,
        })),
      );
      setEditingField(null);
      setDeleteConfirmId(null);
    } else if (open) {
      setFields([]);
      setEditingField(null);
      setDeleteConfirmId(null);
    }
  }, [open, fieldGroup.fieldDefinitions]);

  // Reset tab when modal closes
  useEffect(() => {
    if (!open) {
      setActiveTab("general");
    }
  }, [open]);

  const hasGeneralChanges =
    name.trim() !== fieldGroup.name ||
    (description.trim() || null) !== (fieldGroup.description ?? null) ||
    slugPrefix.trim() !== fieldGroup.slugPrefix;

  const handleSaveGeneral = async () => {
    if (!name.trim() || !slugPrefix.trim()) return;

    const result = await updateFieldGroup(fieldGroup.id, {
      name: name.trim(),
      slugPrefix: slugPrefix.trim(),
      description: description.trim() || null,
    });

    if (result) {
      onSuccess();
      onOpenChange(false);
    }
  };

  // Field handlers
  const handleAddField = () => {
    const newField: LocalFieldDefinition = {
      id: `new-${Date.now()}`,
      name: "",
      fieldType: "text",
      fieldConfig: { max_length: 1000 },
      isRequired: false,
      displayOrder: fields.length,
      isNew: true,
      isEditing: true,
    };
    setFields([...fields, newField]);
    setEditingField(newField);
  };

  const handleEditField = (field: LocalFieldDefinition) => {
    setEditingField({
      ...field,
      fieldConfig: field.fieldConfig ?? {},
      isEditing: true,
    });
  };

  const handleCancelEdit = () => {
    if (editingField?.isNew) {
      setFields(fields.filter((f) => f.id !== editingField.id));
    }
    setEditingField(null);
  };

  const handleSaveField = async () => {
    if (!editingField || !editingField.name.trim()) return;

    if (editingField.isNew) {
      const success = await createFieldDefinition(fieldGroup.id, {
        name: editingField.name.trim(),
        fieldType: editingField.fieldType,
        fieldConfig: { ...editingField.fieldConfig },
        isRequired: editingField.isRequired,
        displayOrder: editingField.displayOrder,
      });
      if (success) {
        onSuccess();
        setEditingField(null);
      }
    } else {
      const success = await updateFieldDefinition(
        fieldGroup.id,
        editingField.id,
        {
          name: editingField.name.trim(),
          fieldConfig: { ...editingField.fieldConfig },
          isRequired: editingField.isRequired,
          displayOrder: editingField.displayOrder,
        },
      );
      if (success) {
        onSuccess();
        setEditingField(null);
      }
    }
  };

  const handleDeleteField = async (fieldId: string) => {
    if (fieldId.startsWith("new-")) {
      setFields(fields.filter((f) => f.id !== fieldId));
      setDeleteConfirmId(null);
      return;
    }

    const success = await deleteFieldDefinition(fieldGroup.id, fieldId);
    if (success) {
      onSuccess();
      setDeleteConfirmId(null);
    }
  };

  const handleMoveField = async (fieldId: string, direction: "up" | "down") => {
    const index = fields.findIndex((f) => f.id === fieldId);
    if (index === -1) return;
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === fields.length - 1) return;

    const newIndex = direction === "up" ? index - 1 : index + 1;
    const newFields = [...fields];
    const [moved] = newFields.splice(index, 1);
    newFields.splice(newIndex, 0, moved);

    const updatedFields = newFields.map((f, i) => ({ ...f, displayOrder: i }));
    setFields(updatedFields);

    const field1 = updatedFields[index];
    const field2 = updatedFields[newIndex];

    if (!field1.id.startsWith("new-")) {
      await updateFieldDefinition(fieldGroup.id, field1.id, {
        displayOrder: field1.displayOrder,
      });
    }
    if (!field2.id.startsWith("new-")) {
      await updateFieldDefinition(fieldGroup.id, field2.id, {
        displayOrder: field2.displayOrder,
      });
    }
    onSuccess();
  };

  const renderFieldTypeIcon = (type: FieldTypeValue) => {
    return FIELD_TYPE_CONFIG[type]?.icon || <Type className="h-4 w-4" />;
  };

  const renderGeneralTab = () => (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <Label
          htmlFor="library-name"
          className="text-xs font-medium uppercase tracking-wide text-stone-500"
        >
          Library Name *
        </Label>
        <Input
          id="library-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Risks, Systems, Processes"
          className="h-10"
        />
      </div>

      <div className="space-y-1.5">
        <Label
          htmlFor="slug-prefix"
          className="text-xs font-medium uppercase tracking-wide text-stone-500"
        >
          ID Prefix *
        </Label>
        <Input
          id="slug-prefix"
          value={slugPrefix}
          onChange={(e) => setSlugPrefix(e.target.value.toUpperCase())}
          placeholder="e.g., RPT, SYS, PROC"
          maxLength={10}
          className="h-10 font-mono"
        />
        <p className="text-xs text-stone-400 mt-1.5">
          Items numbered as {slugPrefix || "XXX"}-001, {slugPrefix || "XXX"}
          -002, etc.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label
          htmlFor="library-description"
          className="text-xs font-medium uppercase tracking-wide text-stone-500"
        >
          Description
        </Label>
        <Textarea
          id="library-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this library used for?"
          rows={3}
          className="resize-none"
        />
      </div>

      <div className="pt-4 border-t border-stone-100">
        <Button
          onClick={handleSaveGeneral}
          disabled={
            !name.trim() || !slugPrefix.trim() || !hasGeneralChanges || loading
          }
          loading={loadingUpdate}
          className="w-full h-10"
        >
          Save Changes
        </Button>
      </div>
    </div>
  );

  const renderFieldEditor = (field: LocalFieldDefinition) => {
    return (
      <div
        key={field.id}
        className="p-4 bg-white border-2 border-stone-900 space-y-4"
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-stone-900 uppercase tracking-wide">
            {field.isNew ? "New Field" : "Edit Field"}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-stone-400 hover:text-stone-600"
            onClick={handleCancelEdit}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-1.5">
          <Label
            htmlFor="field-name"
            className="text-xs font-medium uppercase tracking-wide text-stone-500"
          >
            Field Name *
          </Label>
          <Input
            id="field-name"
            value={field.name}
            onChange={(e) => {
              setEditingField({
                ...field,
                name: e.target.value,
              });
            }}
            placeholder="e.g., Risk Level"
            className="h-9"
          />
        </div>

        {field.isNew ? (
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wide text-stone-500">
              Field Type
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(FIELD_TYPE_CONFIG) as FieldTypeValue[]).map(
                (type) => {
                  const config = FIELD_TYPE_CONFIG[type];
                  const isSelected = field.fieldType === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        const defaultConfig: FieldConfig = {};
                        if (type === "text") {
                          defaultConfig.max_length = 1000;
                        } else if (type === "user") {
                          defaultConfig.allow_multiple = false;
                        } else if (type === "date") {
                          defaultConfig.include_time = false;
                        }
                        setEditingField({
                          ...field,
                          fieldType: type,
                          fieldConfig: defaultConfig,
                        });
                      }}
                      className={`flex flex-col items-center gap-1.5 p-3 border transition-all ${
                        isSelected
                          ? "border-stone-900 bg-stone-900 text-white"
                          : "border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:bg-stone-50"
                      }`}
                    >
                      {config.icon}
                      <span className="text-[10px] font-medium">
                        {config.label}
                      </span>
                    </button>
                  );
                },
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wide text-stone-500">
              Field Type
            </Label>
            <div className="flex items-center gap-2 p-3 bg-stone-50 border border-stone-200 text-stone-600">
              {FIELD_TYPE_CONFIG[field.fieldType].icon}
              <span className="text-sm">
                {FIELD_TYPE_CONFIG[field.fieldType].label}
              </span>
            </div>
          </div>
        )}

        {field.fieldType === "user" && (
          <div className="flex items-center gap-3 py-2">
            <Switch
              id="allow-multiple"
              checked={field.fieldConfig.allow_multiple ?? false}
              onCheckedChange={(checked) =>
                setEditingField({
                  ...field,
                  fieldConfig: {
                    ...field.fieldConfig,
                    allow_multiple: checked,
                  },
                })
              }
            />
            <Label
              htmlFor="allow-multiple"
              className="text-sm text-stone-600 cursor-pointer"
            >
              Allow multiple users
            </Label>
          </div>
        )}

        {field.fieldType === "date" && (
          <div className="flex items-center gap-3 py-2">
            <Switch
              id="include-time"
              checked={field.fieldConfig.include_time ?? false}
              onCheckedChange={(checked) =>
                setEditingField({
                  ...field,
                  fieldConfig: { ...field.fieldConfig, include_time: checked },
                })
              }
            />
            <Label
              htmlFor="include-time"
              className="text-sm text-stone-600 cursor-pointer"
            >
              Include time
            </Label>
          </div>
        )}

        <div className="flex items-center justify-end pt-2 border-t border-stone-100 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancelEdit}
            className="h-8"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSaveField}
            disabled={!field.name.trim() || loading}
            className="h-8"
          >
            {field.isNew ? "Create Field" : "Save Changes"}
          </Button>
        </div>
      </div>
    );
  };

  const renderFieldsTab = () => (
    <div className="space-y-4">
      {fields.length === 0 && !editingField ? (
        <div className="flex flex-col items-center justify-center py-12 text-stone-400">
          <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center mb-3">
            <List className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-stone-600">No custom fields</p>
          <p className="text-xs text-stone-400 mt-1">
            Add fields to capture additional data
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {fields.map((field, index) => {
            const isCurrentlyEditing = editingField?.id === field.id;
            const isDeleting = deleteConfirmId === field.id;

            if (isCurrentlyEditing && editingField) {
              return renderFieldEditor(editingField);
            }

            return (
              <div
                key={field.id}
                data-testid="library-settings-field-row"
                className="group relative flex items-center gap-3 p-3 bg-stone-50 border border-stone-200 hover:border-stone-300 transition-colors"
              >
                <div className="flex items-center gap-1 text-stone-300">
                  <GripVertical className="h-4 w-4" />
                </div>

                <div className="flex items-center justify-center w-8 h-8 bg-white border border-stone-200 text-stone-500">
                  {renderFieldTypeIcon(field.fieldType)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-stone-900 truncate">
                      {field.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-stone-500">
                      {FIELD_TYPE_CONFIG[field.fieldType]?.label ||
                        field.fieldType}
                    </span>
                  </div>
                </div>

                {isDeleting ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-stone-500">Delete?</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteField(field.id)}
                      disabled={loading}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2"
                      onClick={() => setDeleteConfirmId(null)}
                    >
                      No
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-stone-400 hover:text-stone-600"
                      onClick={() => handleMoveField(field.id, "up")}
                      disabled={index === 0 || loading}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-stone-400 hover:text-stone-600"
                      onClick={() => handleMoveField(field.id, "down")}
                      disabled={index === fields.length - 1 || loading}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    <div className="w-px h-4 bg-stone-200 mx-1" />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-stone-400 hover:text-stone-600"
                      onClick={() => handleEditField(field)}
                      disabled={loading}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-stone-400 hover:text-red-600"
                      onClick={() => setDeleteConfirmId(field.id)}
                      disabled={loading}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Button
        variant="outline"
        onClick={handleAddField}
        disabled={editingField !== null || loading}
        className="w-full h-10 border-dashed border-stone-300 text-stone-600 hover:text-stone-900 hover:border-stone-400 hover:bg-white"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Field
      </Button>
    </div>
  );

  const tabs = [
    { id: "general" as const, label: "General", icon: Settings },
    {
      id: "fields" as const,
      label: "Fields",
      icon: Layers,
      count: fields.length,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden p-0 [&>button]:hidden">
        <div className="flex h-full">
          {/* Sidebar navigation */}
          <div className="w-48 bg-stone-50 border-r border-stone-200 flex flex-col">
            <DialogHeader className="p-4 pb-3 border-b border-stone-200">
              <DialogTitle className="text-base font-semibold truncate">
                {fieldGroup.name}
              </DialogTitle>
              <p className="text-xs text-stone-500 mt-0.5">Library Settings</p>
            </DialogHeader>

            <nav className="flex-1 p-2 space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-white text-stone-900 shadow-sm border border-stone-200"
                        : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"
                    }`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="flex-1 text-left">{tab.label}</span>
                    {tab.count !== undefined && tab.count > 0 && (
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded-full ${
                          isActive
                            ? "bg-stone-900 text-white"
                            : "bg-stone-200 text-stone-600"
                        }`}
                      >
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content area */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex items-center justify-end p-3 border-b border-stone-100">
              <DialogClose className="rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-stone-950 focus:ring-offset-2">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>
            <div className="flex-1 overflow-y-auto p-6 pt-4">
              {activeTab === "general" && renderGeneralTab()}
              {activeTab === "fields" && renderFieldsTab()}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
